import * as THREE from 'three';
import arena from './subwayArena.json';
import { validateSurfaces, shapeOf } from '../core/surfaces.js';
import { createFloorTexture, createTileTexture, tileFace } from './tiles.js';
import { createTrain } from '../core/train.js';
import { createSubwayTrain } from './subwayTrain.js';
import { createCrowdBodies } from './crowd.js';

/**
 * The subway platform: the first arena that is a place rather than a test rig.
 *
 * Same split as `testArena.js`, and for the same reason. `subwayArena.json`
 * declares where everything is and what it is made of; this module builds the
 * geometry **out of those same numbers**, so a wall you can see is a wall you
 * can be put through and there is no second copy of the layout to drift.
 *
 * What is new here is that the layout is not a box. A platform is long and
 * shallow, walled down one side and open to a drop down the other, with pillars
 * standing in it. Three things followed from that:
 *
 * - **Pillars are `column` surfaces**, not walls. A wall is a boundary you stay
 *   one side of; a pillar is an obstacle you stay outside of, and the direction
 *   out of it depends on where you are standing. `surfaces.js` grew the shape.
 * - **The walkable floor is a rectangle**, declared in `bounds`. A circle
 *   inscribed in a 16x6 platform throws away most of its length.
 * - **The pillars and the pit sit outside that rectangle.** This arena is still
 *   a thing you do to somebody: nobody walks into a pillar or off the edge, and
 *   only a fighter who has been *sent* reaches either.
 *
 * The camera lives across the tracks, which is why the far side of the trench
 * is a low retaining wall rather than a full-height one — a tunnel wall there
 * would stand between the camera and the fight.
 */

export { arena as SUBWAY_ARENA };

/** Metres of wall drawn past each end of a face, so corners meet cleanly. */
const OVERHANG = 0.15;
const WALL_THICKNESS = 0.16;

export function createSubwayArena(config = arena, { onWarn, crowd = null } = {}) {
  try {
    validateSurfaces(config.surfaces);
  } catch (error) {
    // An arena that cannot be collided against is still worth drawing, so the
    // shape of the mistake is visible rather than just absent.
    onWarn?.(error.message);
  }

  const group = new THREE.Group();
  group.name = `arena-${config.id ?? 'subway'}`;

  const colors = config.palette ?? {};
  const platform = config.platform;
  const pit = config.pit;
  const disposables = [];

  const tiles = createTileTexture({ tile: colors.tile, grout: colors.grout });
  if (tiles) disposables.push(tiles, tiles.roughnessTexture);
  const floor = createFloorTexture({ stone: colors.platform ?? '#41454f' });
  if (floor) disposables.push(floor, floor.roughnessTexture);

  const track = (object) => {
    group.add(object);
    return object;
  };

  function material(options) {
    const made = new THREE.MeshStandardMaterial(options);
    disposables.push(made);
    return made;
  }

  function box(width, height, depth, { x = 0, y = 0, z = 0, material: mat, name, casts = true }) {
    const geometry = new THREE.BoxGeometry(width, height, depth);
    disposables.push(geometry);
    const mesh = new THREE.Mesh(geometry, mat);
    mesh.position.set(x, y, z);
    if (name) mesh.name = name;
    // A slab over the whole platform blocks every shadow-casting light there
    // is, and the shadow it throws is the shape of the room rather than of
    // anything in it. The ceiling lights the platform; it does not shade it.
    mesh.castShadow = casts;
    mesh.receiveShadow = true;
    return track(mesh);
  }

  // --- the platform slab ----------------------------------------------------

  const platformWidth = platform.maxX - platform.minX;
  const platformDepth = platform.maxZ - platform.minZ;
  const platformMid = {
    x: (platform.minX + platform.maxX) / 2,
    z: (platform.minZ + platform.maxZ) / 2,
  };

  box(platformWidth, platform.thickness, platformDepth, {
    x: platformMid.x,
    y: -platform.thickness / 2,
    z: platformMid.z,
    // Stone tiles, each its own shade, worn smooth where people walk (step 98).
    material: material({
      color: floor ? 0xffffff : new THREE.Color(colors.platform ?? '#41454f'),
      map: floor ? tileFace(floor, platformWidth, platformDepth) : null,
      roughnessMap: floor ? tileFace(floor.roughnessTexture, platformWidth, platformDepth) : null,
      roughness: floor ? 1 : 0.94,
      metalness: 0.02,
    }),
    name: 'platform-floor',
  });

  // The yellow strip along the lip. Every station has one, and here it is also
  // the only warning the player gets that the floor stops — so when a service
  // is coming it stops being a line on the floor and starts being a warning.
  let edgeMaterial = null;
  if (config.safetyEdge) {
    const edge = config.safetyEdge;
    edgeMaterial = material({
      color: new THREE.Color(colors.edge ?? '#e2b12a'),
      roughness: 0.6,
      metalness: 0.05,
      emissive: new THREE.Color(colors.edge ?? '#e2b12a'),
      emissiveIntensity: 0.12,
    });
    box(platformWidth, 0.02, edge.maxZ - edge.minZ, {
      x: platformMid.x,
      y: 0.011,
      z: (edge.minZ + edge.maxZ) / 2,
      material: edgeMaterial,
      name: 'safety-edge',
      casts: false,
    });
  }

  // --- the track pit --------------------------------------------------------

  const pitFloorY = pit.floor;
  const pitWidth = pit.maxX - pit.minX + pit.runout * 2;
  const pitDepth = pit.maxZ - pit.minZ;
  const pitMid = { x: (pit.minX + pit.maxX) / 2, z: (pit.minZ + pit.maxZ) / 2 };

  box(pitWidth, 0.12, pitDepth, {
    x: pitMid.x,
    y: pitFloorY - 0.06,
    z: pitMid.z,
    material: material({
      color: new THREE.Color(colors.ballast ?? '#20232a'),
      roughness: 1,
      metalness: 0,
    }),
    name: 'pit-floor',
  });

  // Sleepers and rails, running the whole length including the run-out, so the
  // track reads as continuing into the tunnel rather than stopping at the wall.
  const sleeperMaterial = material({
    color: new THREE.Color(colors.sleeper ?? '#3a3128'),
    roughness: 1,
    metalness: 0,
  });
  const railGauge = pit.rails ?? [];
  const sleeperSpan = railGauge.length >= 2 ? Math.abs(railGauge[1] - railGauge[0]) + 0.5 : 1.6;
  const sleeperGeometry = new THREE.BoxGeometry(0.22, 0.09, sleeperSpan);
  disposables.push(sleeperGeometry);
  const sleeperCount = Math.floor(pitWidth / (pit.sleeperSpacing ?? 0.7));
  const sleepers = new THREE.InstancedMesh(sleeperGeometry, sleeperMaterial, sleeperCount);
  sleepers.name = 'pit-sleepers';
  sleepers.receiveShadow = true;
  const sleeperAt = new THREE.Matrix4();
  const railMid = railGauge.length >= 2 ? (railGauge[0] + railGauge[1]) / 2 : pitMid.z;
  for (let i = 0; i < sleeperCount; i++) {
    sleeperAt.makeTranslation(
      pitMid.x - pitWidth / 2 + (i + 0.5) * (pitWidth / sleeperCount),
      pitFloorY + 0.045,
      railMid
    );
    sleepers.setMatrixAt(i, sleeperAt);
  }
  sleepers.instanceMatrix.needsUpdate = true;
  track(sleepers);

  const railMaterial = material({
    color: new THREE.Color(colors.rail ?? '#9aa1ab'),
    roughness: 0.35,
    metalness: 0.75,
  });
  for (const [index, z] of railGauge.entries()) {
    box(pitWidth, 0.09, 0.07, {
      x: pitMid.x,
      y: pitFloorY + 0.13,
      z,
      material: railMaterial,
      name: `rail-${index}`,
    });
  }

  // The platform's own cut face, so the drop reads as a drop.
  box(platformWidth, platform.thickness, 0.06, {
    x: platformMid.x,
    y: -platform.thickness / 2,
    z: platform.maxZ + 0.03,
    material: material({
      color: new THREE.Color(colors.platform ?? '#41454f').multiplyScalar(0.7),
      roughness: 0.95,
      metalness: 0,
    }),
    name: 'platform-lip',
  });

  // --- the tunnel mouths ----------------------------------------------------
  // Flat black, past each end of the platform. They do nothing but stop the
  // track running out into an empty grey void, and they are where the train
  // comes from.

  const tunnelMaterial = material({
    color: new THREE.Color(colors.tunnel ?? '#05060a'),
    roughness: 1,
    metalness: 0,
  });
  for (const side of [-1, 1]) {
    box(0.4, config.wallHeight + Math.abs(pitFloorY), pitDepth + 0.6, {
      x: pitMid.x + side * (pit.maxX - pit.minX) / 2 + side * 0.2,
      y: (config.wallHeight - Math.abs(pitFloorY)) / 2,
      z: pitMid.z,
      material: tunnelMaterial,
      name: `tunnel-mouth-${side < 0 ? 'west' : 'east'}`,
      casts: false,
    });
  }

  // --- the ceiling ----------------------------------------------------------
  // Over the platform only. The camera watches from across the tracks, and a
  // slab that reached over the trench would be the first thing in shot.

  box(platformWidth, 0.2, platformDepth, {
    x: platformMid.x,
    y: (config.ceilingHeight ?? config.wallHeight) + 0.1,
    z: platformMid.z,
    material: material({
      color: new THREE.Color(colors.ceiling ?? '#2b2e36'),
      roughness: 1,
      metalness: 0,
    }),
    name: 'ceiling',
    casts: false,
  });

  // --- the lights -----------------------------------------------------------
  // The arena owns them. Until now the stage's one directional key light was
  // the whole of it, which is fine for a fight on an open plain and useless the
  // moment there is a roof: the sun does not get in, and everything under it
  // renders black. A place lights itself.

  const lights = new THREE.Group();
  lights.name = 'subway-lights';
  const lighting = config.lighting ?? {};

  if (lighting.ambient) {
    lights.add(
      new THREE.AmbientLight(
        new THREE.Color(lighting.ambient.color),
        lighting.ambient.intensity
      )
    );
  }
  if (lighting.bounce) {
    // Cool from the strip lights above, dark from the concrete below. It is
    // what stops the undersides of the fighters going to pure black.
    lights.add(
      new THREE.HemisphereLight(
        new THREE.Color(lighting.bounce.sky),
        new THREE.Color(lighting.bounce.ground),
        lighting.bounce.intensity
      )
    );
  }
  if (lighting.key) {
    // Angled in from over the tracks, which is the one direction with no
    // ceiling in the way, and the only light here that casts a shadow.
    const key = new THREE.DirectionalLight(
      new THREE.Color(lighting.key.color),
      lighting.key.intensity
    );
    key.position.set(lighting.key.position.x, lighting.key.position.y, lighting.key.position.z);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 1;
    key.shadow.camera.far = 40;
    key.shadow.camera.left = -10;
    key.shadow.camera.right = 10;
    key.shadow.camera.top = 10;
    key.shadow.camera.bottom = -10;
    key.shadow.bias = -0.0005;
    lights.add(key);
  }

  const lamps = lighting.lamps;
  if (lamps) {
    const lampColor = new THREE.Color(lamps.color);
    const step = platformWidth / lamps.count;
    for (let i = 0; i < lamps.count; i++) {
      const x = platform.minX + (i + 0.5) * step;
      const lamp = new THREE.PointLight(
        lampColor,
        lamps.intensity,
        lamps.distance,
        lamps.decay ?? 2
      );
      lamp.position.set(x, lamps.y, lamps.z);
      lights.add(lamp);
    }

    // The fittings the light is supposedly coming out of. Emissive rather than
    // lit, so they read as the source even where a point light has fallen off.
    const stripMaterial = material({
      color: lampColor,
      emissive: lampColor,
      emissiveIntensity: 1.6,
      roughness: 1,
      metalness: 0,
    });
    for (const [index, z] of (lamps.stripZ ?? []).entries()) {
      box(platformWidth - 0.6, 0.06, lamps.stripWidth ?? 0.24, {
        x: platformMid.x,
        y: (config.ceilingHeight ?? config.wallHeight) - 0.03,
        z,
        material: stripMaterial,
        name: `ceiling-strip-${index}`,
        casts: false,
      });
    }
  }
  group.add(lights);

  // --- the surfaces, built from the collision's own data --------------------

  const built = [];
  for (const surface of config.surfaces) {
    const height = surface.height ?? config.wallHeight ?? 3;
    const base = surface.base ?? 0;

    if (shapeOf(surface) === 'column') {
      const geometry = new THREE.CylinderGeometry(
        surface.radius,
        surface.radius,
        height,
        16
      );
      disposables.push(geometry);
      const mesh = new THREE.Mesh(
        geometry,
        material({
          color: new THREE.Color(colors.pillar ?? '#596070'),
          roughness: 0.7,
          metalness: 0.3,
        })
      );
      mesh.name = `pillar-${surface.id}`;
      mesh.position.set(surface.center.x, base + height / 2, surface.center.z);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      track(mesh);
      built.push({ surface, mesh });
      continue;
    }

    const { from, to, normal } = surface;
    const span = Math.hypot(to.x - from.x, to.z - from.z) + OVERHANG * 2;
    const geometry = new THREE.BoxGeometry(span, height, WALL_THICKNESS);
    disposables.push(geometry);

    const mesh = new THREE.Mesh(
      geometry,
      material({
        color: surface.tiled
          ? 0xffffff
          : new THREE.Color(colors.platform ?? '#41454f').multiplyScalar(0.8),
        map: surface.tiled ? tileFace(tiles, span, height) : null,
        roughnessMap: surface.tiled && tiles ? tileFace(tiles.roughnessTexture, span, height) : null,
        roughness: surface.tiled ? (tiles ? 1 : 0.45) : 0.9,
        metalness: 0.04,
      })
    );
    if (mesh.material.map) disposables.push(mesh.material.map);
    if (mesh.material.roughnessMap) disposables.push(mesh.material.roughnessMap);
    mesh.name = `wall-${surface.id}`;
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    // Centred on the face, pushed half a thickness *out* of the arena, so the
    // playable side of the wall is exactly the plane the collision uses.
    mesh.position.set(
      (from.x + to.x) / 2 - normal.x * (WALL_THICKNESS / 2),
      base + height / 2,
      (from.z + to.z) / 2 - normal.z * (WALL_THICKNESS / 2)
    );
    mesh.rotation.y = Math.atan2(-normal.x, -normal.z);

    track(mesh);
    built.push({ surface, mesh });
  }

  // --- the train ------------------------------------------------------------
  // The state machine is `src/core/train.js` and knows nothing about meshes;
  // the rig follows it. The arena owns both, because a train is a thing this
  // station has and not a thing the game has.

  const train = createTrain({ pit: config.pit, ...(config.train ?? {}) });
  const trainRig = createSubwayTrain(train, { pit: config.pit, palette: colors });
  group.add(trainRig.object3D);

  // --- the crowd ------------------------------------------------------------
  // Where people stand is the station's business; how loud they are is not.
  // The mood comes in from outside — it is a function of the fighters, and a
  // platform has no opinion about who is winning.
  const bodies =
    crowd && config.crowd
      ? createCrowdBodies(crowd, {
          config: config.crowd,
          // The same faces the collision reads, so the crowd parts around the
          // pillars without anybody writing down where the pillars are twice.
          surfaces: config.surfaces,
          palette: colors,
        })
      : null;
  if (bodies) group.add(bodies.object3D);

  return {
    id: config.id ?? 'subway',
    name: config.name ?? 'Subway Platform',
    object3D: group,
    train,
    /** The plain data the collision reads. Same source as the geometry. */
    surfaces: config.surfaces,
    bounds: config.bounds,
    spawns: config.spawns,
    camera: config.camera,
    pit: config.pit,
    /** The finish this arena has and no other does: the track, with a train in it. */
    signature: config.signature ?? null,
    /** The spectators, or null in an arena nobody came to watch. */
    crowd: bodies,
    config,
    walls: built,
    /** This arena brings its own floor; the stage's default grid must go. */
    ground: false,
    /** And its own lights, so the stage's outdoor key light must go too. */
    lit: true,

    /**
     * One frame of the things in this arena that move on their own.
     *
     * Only the train, so far. It is deliberately *not* driven by the fight: a
     * service runs whether or not anybody is fighting, and it keeps running
     * through a hitstop, because a train does not stop for a good punch.
     */
    update(dt) {
      train.update(dt);
      trainRig.update();
      bodies?.update(dt);
      // The line on the floor lights up with the service. The headlights come
      // out of one tunnel and the camera is usually pointed somewhere else, so
      // this is the half of the telegraph that is always in shot.
      if (edgeMaterial) edgeMaterial.emissiveIntensity = 0.12 + train.glow * 1.5;
    },

    dispose() {
      trainRig.dispose();
      bodies?.dispose();
      for (const item of disposables) item.dispose?.();
      group.removeFromParent();
    },
  };
}
